import { Button } from "@/components/ui/button";
import { Check, Sparkles, Shield, Clock, Info } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const benefits = [
  "Unlimited access to 50+ courses",
  "Video lessons & PDF resources",
  "Verified completion certificates",
  "Lifetime access to all content",
  "Earn ₦4,000 per referral",
  "Exclusive community access",
  "Direct instructor support",
  "Priority customer service",
];

const Pricing = () => {
  return (
    <section className="py-24 bg-background">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">Simple Pricing</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-foreground mt-4 mb-6">
              One Payment,{" "}
              <span className="text-gradient-hero">Lifetime Access</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              No subscriptions. No hidden fees. Pay once and unlock everything forever.
            </p>
          </div>

          {/* Pricing Card */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-hero opacity-20 blur-3xl rounded-3xl" />
            
            <div className="relative bg-card rounded-3xl shadow-elevated overflow-hidden">
              {/* Popular Badge */}
              <div className="absolute top-0 right-0 bg-gradient-gold px-6 py-2 rounded-bl-2xl">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-accent-foreground" />
                  <span className="text-sm font-semibold text-accent-foreground">Best Value</span>
                </div>
              </div>

              <div className="p-10 md:p-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-10 pb-10 border-b border-border">
                  <div>
                    <h3 className="font-display text-2xl font-semibold text-foreground mb-2">
                      Full Access Pass
                    </h3>
                    <p className="text-muted-foreground">
                      Everything you need to learn & earn
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl md:text-6xl font-bold text-foreground">₦10,250</span>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="inline-flex items-center gap-1.5 text-muted-foreground mt-1 cursor-help hover:text-foreground transition-colors">
                            <span>₦10,000 + ₦250 fee</span>
                            <Info className="w-4 h-4" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs text-center">
                          <p>The ₦250 covers payment processing fees charged by Paystack for secure transactions.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>

                {/* Benefits Grid */}
                <div className="grid md:grid-cols-2 gap-4 mb-10">
                  {benefits.map((benefit) => (
                    <div key={benefit} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <Link to="/signup">
                  <Button variant="hero" size="xl" className="w-full">
                    Pay ₦10,250 & Start Learning
                  </Button>
                </Link>

                {/* Trust Badges */}
                <div className="flex flex-wrap items-center justify-center gap-6 mt-8 pt-8 border-t border-border">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="text-sm">Secure Payment</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-5 h-5 text-primary" />
                    <span className="text-sm">Instant Access</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
